import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Imperative Leaflet preview of searched businesses (CircleMarker, no icon assets).
// L.map + map.remove() in cleanup clears Leaflet's internal _leaflet_id on the DOM node,
// so React 18 StrictMode's mount→cleanup→mount double-invoke re-inits cleanly
// (the declarative <MapContainer> reused the same node and threw "already initialized").
const escapeHtml = (s) =>
  String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export default function BizMap({ points, selectedId, onSelect, height = 220 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const pts = (points || []).filter((p) => typeof p.lat === "number" && typeof p.lng === "number");
  const hasPts = pts.length > 0;
  const sig = pts.map((p) => p.place_id).join(",");

  useEffect(() => {
    if (!hasPts || !containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { scrollWheelZoom: false }).setView([39.5, -98.35], 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "&copy; OpenStreetMap" }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; layerRef.current = null; };
  }, [hasPts]);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer || !hasPts) return;
    layer.clearLayers();
    pts.forEach((p) => {
      const active = selectedId && p.place_id === selectedId;
      const m = L.circleMarker([p.lat, p.lng], {
        radius: active ? 10 : 7,
        color: active ? "#1877F2" : "#B8791B",
        fillColor: active ? "#1877F2" : "#E0A94A",
        fillOpacity: 0.85,
        weight: 2,
      });
      m.bindPopup(`<strong>${escapeHtml(p.name)}</strong><br/><span style="font-size:12px">${escapeHtml(p.address)}</span>`);
      m.on("click", () => onSelectRef.current && onSelectRef.current(p));
      m.addTo(layer);
    });
    map.setView([pts[0].lat, pts[0].lng], 12);
    setTimeout(() => map.invalidateSize(), 0);
  }, [sig, selectedId, hasPts]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasPts) return null;
  return (
    <div style={{ borderRadius: 8, overflow: "hidden", border: "1px solid var(--border)", marginTop: 10 }} data-testid="biz-map">
      <div ref={containerRef} style={{ height, width: "100%" }} />
    </div>
  );
}
